import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, logoutUser, loadUser, clearError, type AuthState } from '../store/slices/authSlice';
import type { RootState, AppDispatch } from '../store';
import type { User } from '../types';

export interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, token, isAuthenticated, isLoading, error } = useSelector<RootState, AuthState>(
    (state) => state.auth
  );

  useEffect(() => {
    // Load user data if we have a token but no user data
    if (token && !user && !isLoading) {
      dispatch(loadUser());
    }
  }, [dispatch, token, user, isLoading]);

  // Check for existing token on mount and validate it
  useEffect(() => {
    const existingToken = localStorage.getItem('auth_token');
    if (existingToken && !token) {
      // Token exists in localStorage but not in Redux state, dispatch loadUser
      dispatch(loadUser());
    }
  }, [dispatch, token]);

  const login = async (email: string, password: string) => {
    await dispatch(loginUser({ email, password })).unwrap();
  };

  const logout = async () => {
    await dispatch(logoutUser());
  };

  const handleClearError = () => {
    dispatch(clearError());
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading: isLoading,
      error,
      isAuthenticated,
      login,
      logout,
      clearError: handleClearError,
    }),
    [user, token, isLoading, error, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
